import type { Edge, Node } from '@xyflow/react';
import type { AssetGraphResponseDto } from '@terra/shared-contracts-external-network';
import { APPLICATION_NODE_ID_PREFIX } from '../flow-layout/constants';
import type { AssetNodeData } from './constants';

export type GraphWebAppCluster = {
  application?: { id: string; name: string; isDraft: boolean } | null;
  members: Array<{ domainId: string | null; originUrl: string }>;
};

function hostnameFromOrigin(originUrl: string): string | undefined {
  try {
    return new URL(originUrl).hostname.toLowerCase();
  } catch {
    return undefined;
  }
}

export function appendApplicationNodes({
  graph,
  clusters,
  flowNodes,
  flowEdges,
  translateEdgeType,
  showEdgeLabels,
}: {
  graph: AssetGraphResponseDto;
  clusters: GraphWebAppCluster[];
  flowNodes: Node<AssetNodeData>[];
  flowEdges: Edge[];
  translateEdgeType: (edgeType: string) => string;
  showEdgeLabels: boolean;
}) {
  const domainNodes = graph.nodes.filter((node) => node.kind === 'DOMAIN');

  for (const cluster of clusters) {
    if (!cluster.application) {
      continue;
    }

    const applicationNodeId = `${APPLICATION_NODE_ID_PREFIX}${cluster.application.id}`;
    const hostNodeIds = domainNodes
      .filter((node) =>
        cluster.members.some((member) => {
          if (member.domainId && member.domainId === node.id) {
            return true;
          }

          const hostname = hostnameFromOrigin(member.originUrl);

          return (
            hostname !== undefined &&
            node.properties.fqdn?.toLowerCase() === hostname
          );
        }),
      )
      .map((node) => node.id);

    if (hostNodeIds.length === 0) {
      continue;
    }

    const anchor = flowNodes.find((node) => hostNodeIds.includes(node.id));

    flowNodes.push({
      id: applicationNodeId,
      type: 'externalNetworkNode',
      width: 180,
      height: 110,
      position: anchor
        ? { x: anchor.position.x + 220, y: anchor.position.y }
        : { x: 0, y: 0 },
      data: {
        label: cluster.application.name,
        entityKind: 'APPLICATION',
        signalsCount: 0,
        applicationId: cluster.application.id,
        isDraft: cluster.application.isDraft,
      } satisfies AssetNodeData,
    });

    for (const hostNodeId of hostNodeIds) {
      flowEdges.push({
        id: `${hostNodeId}-${applicationNodeId}`,
        source: hostNodeId,
        target: applicationNodeId,
        type: 'externalNetworkEdge',
        label: showEdgeLabels
          ? translateEdgeType('HOSTS_APPLICATION')
          : undefined,
        animated: false,
        data: { edgeType: 'HOSTS_APPLICATION' },
      });
    }
  }
}
